import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import { BorderShapeId, LegsShapeId } from '@core/constants/Shape';
import { MaterialId } from '@core/constants/Material';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
} from '../../interfaces/Form';
import fieldsContact from './common/fieldsContact';
import { additionalDataFactory } from './common/fieldsAdditionalData';
import { tableRectangleTrayShapeDimension } from './common/fieldsTrayShapeDimension';
import {
    allMetals,
    allSolidWoods,
    allWoods,
    getMaterialsWithExceptions,
} from './common/materialGroups';
import allLegs, {
    allBorders,
    getBordersWithExceptions,
    getLegsWithExceptions,
} from './common/shapesGroups';
import { fieldsLegsDimension } from './common/fieldsLegsDimension';
import budgetMarksBuilder from './common/budgetMarksBuilder';

const tableIndustrialForm: IForm = {
    formId       : PublishedFormId.TableIndustrial,
    type         : FormTypeId.Table,
    thumbnailURL : '/resized-images/forms/tableIndustrial.jpg',
    imagesURLs   : [
        '/resized-images/forms/description/tableIndustrial/tableIndustrial1.jpg',
        '/resized-images/forms/description/tableIndustrial/tableIndustrial2.jpg',
        '/resized-images/forms/description/tableIndustrial/tableIndustrial3.jpg',
        '/resized-images/forms/description/tableIndustrial/tableIndustrial4.jpg',
        '/resized-images/forms/description/tableIndustrial/tableIndustrial5.jpg',
        '/resized-images/forms/description/tableIndustrial/tableIndustrial6.jpg',
        '/resized-images/forms/description/tableIndustrial/tableIndustrial7.jpg',
        '/resized-images/forms/description/tableIndustrial/tableIndustrial8.jpg',
    ],
    fieldsOrder: [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.TrayShapeDimension,
        IFormFieldKey.TrayMaterial,
        IFormFieldKey.Border,
        IFormFieldKey.LegsShape,
        IFormFieldKey.LegsMaterial,
        IFormFieldKey.LegsDimension,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        trayShapeDimension : tableRectangleTrayShapeDimension,
        trayMaterial       : {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions([
                    ...allWoods,
                    ...allMetals,
                ],
                [
                    MaterialId.BlackLocust,
                    MaterialId.Iroko,
                    MaterialId.Douglas,
                    MaterialId.DouglasRecycled,
                    MaterialId.DouglasEngineered,
                    MaterialId.DouglasVeneer,
                    MaterialId.Padouk,
                    MaterialId.PadoukRecycled,
                    MaterialId.PadoukEngineered,
                    MaterialId.PadoukVeneer,
                    MaterialId.Ipe,
                    MaterialId.IpeRecycled,
                    MaterialId.IpeEngineered,
                    MaterialId.IpeVeneer,
                    MaterialId.Ash,
                    MaterialId.AshRecycled,
                    MaterialId.AshEngineered,
                    MaterialId.AshVeneer,
                    MaterialId.AshOlive,
                    MaterialId.AshOliveEngineered,
                    MaterialId.AshOliveRecycled,
                    MaterialId.AshOliveVeneer,
                    MaterialId.Chestnut,
                    MaterialId.ChestnutEngineered,
                    MaterialId.ChestnutRecycled,
                    MaterialId.ChestnutVeneer,
                    MaterialId.Mahogany,
                    MaterialId.MahoganyEngineered,
                    MaterialId.MahoganyRecycled,
                    MaterialId.MahoganyVeneer,
                    MaterialId.Elm,
                    MaterialId.ElmEngineered,
                    MaterialId.ElmRecycled,
                    MaterialId.ElmVeneer,
                    MaterialId.Alderwood,
                    MaterialId.AlderwoodEngineered,
                    MaterialId.AlderwoodRecycled,
                    MaterialId.AlderwoodVeneer,
                    MaterialId.Birch,
                    MaterialId.BirchEngineered,
                    MaterialId.BirchRecycled,
                    MaterialId.BirchVeneer,
                    MaterialId.Garapa,
                    MaterialId.Zinc,
                    MaterialId.Brass,
                ]),
                required: true,
            },
        },
        border: {
            value   : undefined,
            options : getBordersWithExceptions(allBorders,
                [
                    BorderShapeId.RoundedQuarterCircle,
                    BorderShapeId.RoundedHalfCircle,
                    BorderShapeId.AirplaneWings,
                    BorderShapeId.LiveEdge,
                ]),
            required: true,
        },
        legsShape: {
            value   : undefined,
            options : getLegsWithExceptions(allLegs,
                [
                    LegsShapeId.LegsTrapezeInverted,
                    LegsShapeId.LegsMonastery,
                    LegsShapeId.LegsRightCrossPiece,
                    LegsShapeId.LegsTrestleCross,
                    LegsShapeId.LegsScandinavian,
                    LegsShapeId.LegsSquareCrossPiece,
                ]),
            required: true,
        },
        legsMaterial: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions([
                    ...allSolidWoods,
                    ...allMetals,
                ],
                [
                    MaterialId.BlackLocust,
                    MaterialId.Iroko,
                    MaterialId.Redcedar,
                    MaterialId.Larch,
                    MaterialId.Padouk,
                    MaterialId.Ipe,
                    MaterialId.Ash,
                    MaterialId.AshOlive,
                    MaterialId.Chestnut,
                    MaterialId.Mahogany,
                    MaterialId.Elm,
                    MaterialId.Alderwood,
                    MaterialId.Birch,
                    MaterialId.Garapa,
                    MaterialId.Aluminum,
                    MaterialId.Zinc,
                    MaterialId.Brass,
                ]),
                required: true,
            },
        },
        legsDimension  : fieldsLegsDimension,
        additionalData : additionalDataFactory({
            budgetMin          : 1200,
            budgetMax          : 16000,
            budgetDefaultValue : 2200,
            marks              : budgetMarksBuilder(1200, 100, 6000, 500, 16000),
        }),
        contact: fieldsContact,
    },
};

export default tableIndustrialForm;
